/**
 * One import on the page (decision 56; part 17 log, tables H and T): the file's size checked before anything is read, an
 * import worker started for it alone, and ended at the first of its reply, a failure, the time limits or a cancel; one
 * import at a time, and a new worker for the next (decision 56's restart). A time limit falling due after the page was
 * hidden is set again (T3). A bundle's second run (part 24b, table HR) hands each batch to the page and asks for the next
 * only when the page is done with it; the time limits count only the worker's time. The worker, the timers, the clock and
 * the page's visibility are injected, so the logic runs in tests.
 */
import { IMPORT_LIMITS } from '@cockatrice/core';
import type { ImportFormat } from '@cockatrice/core';
import type { BundlePiece } from '@cockatrice/export/bundle';
import { readMessage } from './protocol.ts';
import type { BundleCheckedReply, ImportedReply, ImportMessage, NextBatch, ReimportedReply, WorkerJob, WorkerProblem } from './protocol.ts';

/** T1: how long the worker may go without a message, in milliseconds, before it is ended. */
export const STALL_MS = 30_000;

/** T2: how long one import may take, in milliseconds, per kind. */
export const IMPORT_TIME_MS: Readonly<Record<ImportFormat, number>> = Object.freeze({ markdown: 120_000, text: 120_000, docx: 120_000, bundle: 1_800_000 });

/** An import worker, as far as the sandbox drives it (a `Worker`). */
export interface ImportWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: WorkerJob | NextBatch): void;
  terminate(): void;
}

/** Why an import gave no files: the worker's problem (P1–P8, P12), or the sandbox's own (P9–P11). */
export type ImportProblem = WorkerProblem | 'timed-out' | 'cancelled' | 'busy';

/** What one import gives: the book made (part 18), what a bundle holds (H6, part 24b), a file re-imported (part 23b), or why there is none. */
export type ImportOutcome = BundleCheckedReply | ImportedReply | ReimportedReply | { readonly kind: 'refused'; readonly problem: ImportProblem };

/** What a bundle's second run gives (table HR): every piece handed to the page, or why not. */
export type BundleReadOutcome = { readonly kind: 'read' } | { readonly kind: 'refused'; readonly problem: ImportProblem };

/** What the sandbox needs; everything with an effect is injected. */
export interface ImportSandboxOptions {
  /** Starts an import worker. */
  readonly worker: () => ImportWorker;
  /** `setTimeout` and `clearTimeout`. */
  readonly setTimer: (run: () => void, ms: number) => unknown;
  readonly clearTimer: (timer: unknown) => void;
  /** Milliseconds from any fixed start (`performance.now`): the worker's time in a bundle's second run (HR13). */
  readonly now: () => number;
  /** Whether the page is hidden (`document.hidden`): a phone freezes a hidden page and its workers together (T3). */
  readonly hidden: () => boolean;
  /** Calls `listener` when the page is shown or hidden (`visibilitychange`); returns what stops it. */
  readonly onVisibility: (listener: () => void) => () => void;
}

/** What one import is asked for beside its file and kind. */
export interface ImportRequest {
  /** Hears each progress message: bytes of the file read, out of its size (table G). */
  readonly onProgress?: (done: number, total: number) => void;
  /** Cancels the import (H3, H14). */
  readonly signal?: AbortSignal;
  /** Part 23b, M″1: the Word file is read as this app's export, to merge into the open book (`format` is `docx`). */
  readonly reimport?: boolean;
}

/** What a bundle's second run is asked for beside its file (table HR). */
export interface BundleReadRequest {
  /** Takes one batch of pieces (table O′'s order); the next is asked for when it settles (HR3–HR6). */
  readonly onBatch: (pieces: readonly BundlePiece[]) => Promise<void>;
  /** Hears each progress message, as {@link ImportRequest.onProgress}. */
  readonly onProgress?: (done: number, total: number) => void;
  /** Cancels the run (HR1, HR7). */
  readonly signal?: AbortSignal;
}

/** What one worker's messages do, beside progress (H8) and the end (table H). */
interface Conversation<O> {
  /** Handles a message that isn't progress; `end` ends the import with an outcome. */
  readonly message: (message: Exclude<ImportMessage, { kind: 'progress' }>, talk: Talk<O>) => void;
  readonly onProgress: ((done: number, total: number) => void) | undefined;
  readonly signal: AbortSignal | undefined;
}

/** What a conversation's handler may do with the worker and the timers. */
interface Talk<O> {
  readonly end: (outcome: O) => void;
  /** HR3: the page holds a batch: the timers stop, and any message ends the import (HR8). */
  readonly pause: () => void;
  /** HR4: `next` posted, the timers going again with what is left of T2; false when the post threw (HR15). */
  readonly resume: () => boolean;
  readonly ended: () => boolean;
}

/** An import that gave nothing, and why. */
interface Refused { readonly kind: 'refused'; readonly problem: ImportProblem }

const refused = (problem: ImportProblem): Refused => ({ kind: 'refused', problem });

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/** Table H: runs imports one at a time, each in a worker of its own. */
export class ImportSandbox {
  readonly #options: ImportSandboxOptions;
  #running = false;

  constructor(options: ImportSandboxOptions) {
    this.#options = options;
  }

  /** Whether an import is running (H2). */
  get running(): boolean {
    return this.#running;
  }

  /** Table H: `file` opened as `format` in a worker of its own, or why not. */
  run(file: Blob, format: ImportFormat, request: ImportRequest = {}): Promise<ImportOutcome> {
    const job: WorkerJob = request.reimport === true ? { kind: 'reimport', file } : { kind: 'open', format, file };
    return this.#converse<ImportOutcome>(file, format, job, {
      onProgress: request.onProgress,
      signal: request.signal,
      message: (message, talk) => {
        // HR17: a batch is no reply, and only a bundle's first run says what a bundle holds.
        if (message.kind === 'bundle-batch' || (message.kind === 'bundle-checked' && format !== 'bundle')) talk.end(refused('failed'));
        // H6, H7.
        else talk.end(message.kind === 'refused' ? refused(message.problem) : message);
      },
    });
  }

  /**
   * Table HR: a bundle's second run over `file`, whose first run's manifest SHA-256 is `manifest`: each batch handed to
   * `request.onBatch`, the next asked for when it settles; `read` once the last is taken, or why not.
   */
  read(file: Blob, manifest: string, request: BundleReadRequest): Promise<BundleReadOutcome> {
    return this.#converse<BundleReadOutcome>(file, 'bundle', { kind: 'bundle-read', file, manifest }, {
      onProgress: request.onProgress,
      signal: request.signal,
      message: (message, talk) => {
        // HR9, HR10.
        if (message.kind !== 'bundle-batch') {
          talk.end(message.kind === 'refused' ? refused(message.problem) : refused('failed'));
          return;
        }
        // HR3.
        talk.pause();
        void (async () => {
          try {
            await request.onBatch(message.pieces);
          } catch {
            // HR6, a throw as much as a rejection.
            talk.end(refused('failed'));
            return;
          }
          // HR7, HR16: the run ended while the page took the batch.
          if (talk.ended()) return;
          // HR5, HR4, HR15.
          if (message.last) talk.end({ kind: 'read' });
          else if (!talk.resume()) talk.end(refused('failed'));
        })();
      },
    });
  }

  /** Tables H and HR: one worker for `file`, sent `job`, its messages handled by `conversation` until the import ends. */
  #converse<O>(file: Blob, format: ImportFormat, job: WorkerJob, conversation: Conversation<O | Refused>): Promise<O | Refused> {
    type Outcome = O | Refused;
    // H1–H3, in that order: nothing is started.
    if (file.size > IMPORT_LIMITS[format].inputBytes) return Promise.resolve(refused('too-large'));
    if (this.#running) return Promise.resolve(refused('busy'));
    if (conversation.signal?.aborted === true) return Promise.resolve(refused('cancelled'));
    let worker: ImportWorker;
    try {
      worker = this.#options.worker();
    } catch {
      // H4.
      return Promise.resolve(refused('failed'));
    }
    this.#running = true;
    return new Promise<Outcome>((resolve) => {
      const { setTimer, clearTimer, hidden, now } = this.#options;
      let ended = false;
      /** HR3, HR8: whether the worker may send (false while the page holds a batch). */
      let waiting = true;
      let done = 0;
      let stall: unknown = undefined;
      let whole: unknown = undefined;
      /** HR13: the worker's time spent before the current wait, and when the current wait began. */
      let spent = 0;
      let since = now();
      // T3: whether the page was hidden since the last message (for T1) and since T2 was set: time it spent frozen
      // isn't the worker's.
      let hiddenSinceMessage = hidden();
      let hiddenSinceWhole = hidden();
      const stopWatching = this.#options.onVisibility(() => {
        if (hidden()) {
          hiddenSinceMessage = true;
          hiddenSinceWhole = true;
        }
      });
      const end = (outcome: Outcome): void => {
        // H15: whatever comes after the end is ignored.
        if (ended) return;
        ended = true;
        clearTimer(stall);
        clearTimer(whole);
        stopWatching();
        conversation.signal?.removeEventListener('abort', cancel);
        worker.terminate();
        this.#running = false;
        resolve(outcome);
      };
      const cancel = (): void => {
        end(refused('cancelled'));
      };
      const restartStall = (): void => {
        clearTimer(stall);
        stall = setTimer(() => {
          // T3: the page hidden meanwhile: the worker may have been frozen with it; it gets another T1.
          if (hiddenSinceMessage || hidden()) {
            hiddenSinceMessage = hidden();
            restartStall();
            return;
          }
          end(refused('timed-out'));
        }, STALL_MS);
      };
      // Set at the start, on resuming, and again only from its own firing: never while another is pending.
      const setWhole = (): void => {
        whole = setTimer(() => {
          if (hiddenSinceWhole || hidden()) {
            hiddenSinceWhole = hidden();
            spent = 0;
            since = now();
            setWhole();
            return;
          }
          end(refused('timed-out'));
        }, IMPORT_TIME_MS[format] - spent);
      };
      const talk: Talk<Outcome> = {
        end,
        pause: () => {
          waiting = false;
          clearTimer(stall);
          clearTimer(whole);
          // HR13, HR14 (deep-fix r1): a worker's turn shown throughout counts; one the page was hidden in may have been
          // frozen with it, so T2 starts afresh, as its own firing does then (T3).
          spent = hiddenSinceWhole || hidden() ? 0 : spent + now() - since;
        },
        resume: () => {
          try {
            worker.postMessage({ kind: 'next' });
          } catch {
            return false;
          }
          waiting = true;
          since = now();
          hiddenSinceMessage = hidden();
          hiddenSinceWhole = hidden();
          restartStall();
          setWhole();
          return true;
        },
        ended: () => ended,
      };
      worker.addEventListener('message', (event) => {
        if (ended) return;
        const message = readMessage(dataOf(event));
        // H9, HR8, HR11: not a message, one while the page holds a batch, or progress going back or counting another
        // total than the file's size.
        if (message === null || !waiting || (message.kind === 'progress' && (message.done < done || message.total !== file.size))) {
          end(refused('failed'));
          return;
        }
        if (message.kind === 'progress') {
          // H8, HR2.
          done = message.done;
          hiddenSinceMessage = hidden();
          restartStall();
          conversation.onProgress?.(message.done, message.total);
          return;
        }
        conversation.message(message, talk);
      });
      // H10, H11.
      worker.addEventListener('error', () => {
        end(refused('failed'));
      });
      worker.addEventListener('messageerror', () => {
        end(refused('failed'));
      });
      // T1, T2, H14.
      restartStall();
      setWhole();
      conversation.signal?.addEventListener('abort', cancel);
      try {
        worker.postMessage(job);
      } catch {
        // H5.
        end(refused('failed'));
      }
    });
  }
}
